/**
 * Renderer, scene, lights and the studio environment.
 *
 * Ported from three.js r128 to r185: colour management is on by default and
 * lights are physical, so every intensity authored against the legacy pipeline
 * is multiplied by `LEGACY_LIGHT_SCALE` (π) — the r155 migration factor that
 * keeps the original brightness.
 */

import {
  BackSide,
  BoxGeometry,
  Color,
  DirectionalLight,
  Fog,
  HemisphereLight,
  Mesh,
  MeshBasicMaterial,
  PCFShadowMap,
  PerspectiveCamera,
  PlaneGeometry,
  PMREMGenerator,
  Scene,
  SphereGeometry,
  SRGBColorSpace,
  WebGLRenderer,
} from "three";

/** r155 made lights physical; multiply legacy intensities by π to match. */
const LEGACY_LIGHT_SCALE = Math.PI;

const SHADOW_MAP_SIZE = 2048;

export type SceneTheme = "dark" | "light";

const BACKGROUND: Readonly<Record<SceneTheme, number>> = {
  light: 0xdfe6ea,
  dark: 0x1b2129,
};

export type SceneContext = Readonly<{
  renderer: WebGLRenderer;
  scene: Scene;
  camera: PerspectiveCamera;
  sun: DirectionalLight;
  dispose: () => void;
}>;

/** A few emissive slabs in a dome — enough for believable aluminium reflections. */
function buildEnvironment(renderer: WebGLRenderer, scene: Scene): () => void {
  const pmrem = new PMREMGenerator(renderer);
  const source = new Scene();
  const dome = new SphereGeometry(50, 16, 8);
  const domeMaterial = new MeshBasicMaterial({ color: 0x9fb0c0, side: BackSide });
  source.add(new Mesh(dome, domeMaterial));

  const lampMaterial = new MeshBasicMaterial({ color: 0xffffff });
  const lampGeometry = new BoxGeometry(1, 1, 1);
  const lamps: readonly (readonly [number, number, number, number, number, number])[] = [
    [0, 12, 0, 0.5, 20, 20],
    [15, 6, 0, 20, 8, 0.5],
    [-15, 6, 0, 20, 8, 0.5],
    [0, 6, 15, 20, 8, 0.5],
  ];
  for (const [x, y, z, w, h, d] of lamps) {
    const lamp = new Mesh(lampGeometry, lampMaterial);
    lamp.position.set(x, y, z);
    lamp.scale.set(w, h, d);
    source.add(lamp);
  }

  const floorGeometry = new PlaneGeometry(100, 100);
  const floor = new Mesh(floorGeometry, new MeshBasicMaterial({ color: 0x6d7378 }));
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -2;
  source.add(floor);

  const target = pmrem.fromScene(source, 0.04);
  scene.environment = target.texture;

  return () => {
    target.dispose();
    pmrem.dispose();
    dome.dispose();
    lampGeometry.dispose();
    floorGeometry.dispose();
    domeMaterial.dispose();
    lampMaterial.dispose();
  };
}

export function createSceneContext(theme: SceneTheme): SceneContext {
  const renderer = new WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.shadowMap.enabled = true;
  // PCFSoftShadowMap is deprecated as of r185 and silently falls back to this.
  renderer.shadowMap.type = PCFShadowMap;

  const scene = new Scene();
  const camera = new PerspectiveCamera(48, 1, 0.05, 400);

  scene.add(new HemisphereLight(0xf2f5f8, 0x8d9298, 0.8 * LEGACY_LIGHT_SCALE));

  const sun = new DirectionalLight(0xffffff, 0.85 * LEGACY_LIGHT_SCALE);
  sun.position.set(18, 30, 12);
  sun.castShadow = true;
  sun.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
  sun.shadow.camera.left = -35;
  sun.shadow.camera.right = 45;
  sun.shadow.camera.top = 45;
  sun.shadow.camera.bottom = -25;
  sun.shadow.camera.updateProjectionMatrix();
  sun.shadow.bias = -0.0004;
  sun.shadow.normalBias = 0.02;
  scene.add(sun);

  const disposeEnvironment = buildEnvironment(renderer, scene);
  applySceneTheme(scene, theme);

  return {
    renderer,
    scene,
    camera,
    sun,
    dispose: () => {
      disposeEnvironment();
      renderer.dispose();
    },
  };
}

export function applySceneTheme(scene: Scene, theme: SceneTheme): void {
  const color = BACKGROUND[theme];

  scene.background = new Color(color);
  scene.fog = new Fog(color, 45, 130);
}
